import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Grid,
} from '@mui/material';
import { Add, Visibility, Store as StoreIcon, Search, Clear } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { TableShimmer } from '../../components/common/Shimmer';
import storeService from '../../services/storeService';
import clusterService from '../../services/clusterService';
import userService from '../../services/userService';
import { Store, CreateStoreRequest, Cluster, User } from '../../types';

// Enhanced Google Maps coordinate input component
const LocationInput: React.FC<{
  selectedLocation: { lat: number; lng: number } | null;
  onLocationSelect: (location: { lat: number; lng: number }) => void;
}> = ({ selectedLocation, onLocationSelect }) => {
  const [lat, setLat] = useState(selectedLocation?.lat?.toString() || '');
  const [lng, setLng] = useState(selectedLocation?.lng?.toString() || '');
  const [searchQuery, setSearchQuery] = useState('');

  const handleLatChange = (value: string) => {
    setLat(value);
    const latNum = parseFloat(value);
    const lngNum = parseFloat(lng);
    if (!isNaN(latNum) && !isNaN(lngNum)) {
      onLocationSelect({ lat: latNum, lng: lngNum });
    }
  };

  const handleLngChange = (value: string) => {
    setLng(value);
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(value);
    if (!isNaN(latNum) && !isNaN(lngNum)) {
      onLocationSelect({ lat: latNum, lng: lngNum });
    }
  };

  const openGoogleMaps = () => {
    if (searchQuery.trim()) {
      // Open Google Maps with search query
      const url = `https://www.google.com/maps/search/${encodeURIComponent(searchQuery)}`;
      window.open(url, '_blank');
    } else if (lat && lng) {
      // Open Google Maps with coordinates
      const url = `https://www.google.com/maps/@${lat},${lng},15z`;
      window.open(url, '_blank');
    } else {
      // Open Google Maps homepage
      window.open('https://www.google.com/maps', '_blank');
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setLat(lat.toString());
          setLng(lng.toString());
          onLocationSelect({ lat, lng });
        },
        (error) => {
          alert('Unable to get your location. Please enter coordinates manually.');
        }
      );
    } else {
      alert('Geolocation is not supported by this browser.');
    }
  };

  return (
    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 3, bgcolor: '#fafafa' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <span style={{ fontSize: '24px', marginRight: '8px' }}>🗺️</span>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Store Location
        </Typography>
      </Box>

      {/* Search Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
          Search for a place
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="e.g., Times Square New York, Mumbai India, Central Park..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
              }
            }}
          />
          <Button
            variant="contained"
            onClick={openGoogleMaps}
            size="small"
            sx={{
              minWidth: '140px',
              background: 'linear-gradient(45deg, #4285f4 30%, #34a853 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #3367d6 30%, #2d8f47 90%)',
              }
            }}
          >
            🔍 Open Google Maps
          </Button>
        </Box>
      </Box>

      {/* Coordinate Input Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
          Enter coordinates manually
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            label="Latitude"
            size="small"
            type="number"
            value={lat}
            onChange={(e) => handleLatChange(e.target.value)}
            inputProps={{ step: "0.000001", min: -90, max: 90 }}
            sx={{ flex: 1 }}
            placeholder="e.g., 28.6139"
            helperText="Range: -90 to 90"
          />
          <TextField
            label="Longitude"
            size="small"
            type="number"
            value={lng}
            onChange={(e) => handleLngChange(e.target.value)}
            inputProps={{ step: "0.000001", min: -180, max: 180 }}
            sx={{ flex: 1 }}
            placeholder="e.g., 77.2090"
            helperText="Range: -180 to 180"
          />
          <Button
            variant="outlined"
            onClick={getCurrentLocation}
            size="small"
            sx={{ minWidth: '120px' }}
          >
            📍 My Location
          </Button>
        </Box>
      </Box>

      {/* Instructions */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>💡 How to get coordinates:</strong>
          <br />
          1. Search for your location above and click "Open Google Maps"
          <br />
          2. Right-click on the exact spot → "What's here?"
          <br />
          3. Copy the coordinates and paste them above
          <br />
          4. Or use "My Location" for GPS coordinates
        </Typography>
      </Alert>

      {/* Success Indicator */}
      {selectedLocation && (
        <Box sx={{ 
          p: 2, 
          bgcolor: 'success.main', 
          borderRadius: 1,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <span>✅</span>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Location Selected: {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const storeSchema = yup.object({
  name: yup.string().required('Store name is required'),
  code: yup.string().required('Store code is required'),
  latitude: yup.number().min(-90).max(90).optional(),
  longitude: yup.number().min(-180).max(180).optional(),
  city: yup.string().required('City is required'),
  state: yup.string().required('State is required'),
  description: yup.string().optional(),
  cluster_id: yup.string().required('Cluster is required'),
  manager_id: yup.string().optional().nullable(),
});

const StoresPage: React.FC = () => {
  const [stores, setStores] = useState<Store[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [storeManagers, setStoreManagers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  
  // Pagination and filters
  const [filters, setFilters] = useState({
    page: 1,
    limit: 25,
    search: '',
    cluster_id: '',
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 25,
    pages: 1,
  });
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'success' | 'error' | 'warning' | 'info',
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateStoreRequest>({
    resolver: yupResolver(storeSchema) as any,
  });

  // Helper functions for snackbar
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'error') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const fetchStores = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: filters.page,
        limit: filters.limit,
      };
      
      if (filters.search) {
        params.search = filters.search;
      }
      if (filters.cluster_id) {
        params.cluster_id = filters.cluster_id;
      }
      
      const response = await storeService.getStores(params);
      setStores(response.stores);
      setPagination(response.pagination || { total: 0, page: 1, limit: 25, pages: 1 });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch stores');
    } finally {
      setLoading(false);
    }
  };

  const fetchClusters = async () => {
    try {
      const response = await clusterService.getClusters();
      setClusters(response.clusters);
    } catch (err: any) {
      console.error('Failed to fetch clusters:', err);
    }
  };

  const fetchStoreManagers = async () => {
    try {
      const response = await userService.getUsers({ role: 'store_manager' });
      setStoreManagers(response.users);
    } catch (err: any) {
      console.error('Failed to fetch store managers:', err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      // Stagger API calls to prevent rate limiting
      await fetchClusters();
      await new Promise(resolve => setTimeout(resolve, 200));
      await fetchStoreManagers();
    };
    loadData();
  }, []);

  // Fetch stores when filters change
  useEffect(() => {
    fetchStores();
  }, [filters.page, filters.limit, filters.search, filters.cluster_id]);

  const handleCreateStore = async (data: CreateStoreRequest) => {
    try {
      // Check if we have coordinates either from selectedLocation or from form data
      let warehouse_latitude, warehouse_longitude;
      
      if (selectedLocation) {
        warehouse_latitude = selectedLocation.lat;
        warehouse_longitude = selectedLocation.lng;
      } else if (data.latitude && data.longitude) {
        warehouse_latitude = data.latitude;
        warehouse_longitude = data.longitude;
      } else {
        showSnackbar('Please provide store location coordinates', 'warning');
        return;
      }
      
      const storeData = {
        ...data,
        warehouse_latitude,
        warehouse_longitude,
        // Convert empty string to null for manager_id
        manager_id: data.manager_id && data.manager_id.trim() !== '' ? data.manager_id : null,
      };
      
      console.log('Creating store with data:', storeData); // Debug log
      
      const response = await storeService.createStore(storeData);
      setOpenDialog(false);
      setSelectedLocation(null);
      reset();
      setError(null);
      showSnackbar('Store created successfully!', 'success');
      // Refresh stores list
      fetchStores();
    } catch (err: any) {
      console.error('Store creation error:', err); // Debug log
      
      // Handle specific backend validation errors
      const errorMessage = err.response?.data?.message || 'Failed to create store';
      
      // Check for specific error types and show appropriate messages
      if (errorMessage.includes('already assigned')) {
        showSnackbar('This Store Manager is already assigned to another store. Please select a different manager.', 'warning');
      } else if (errorMessage.includes('Store Manager not found')) {
        showSnackbar('Selected Store Manager not found. Please refresh and try again.', 'error');
      } else if (errorMessage.includes('Cluster not found')) {
        showSnackbar('Selected Cluster not found. Please refresh and try again.', 'error');
      } else if (errorMessage.includes('Store code already exists')) {
        showSnackbar('Store code already exists. Please use a different code.', 'warning');
      } else {
        showSnackbar(errorMessage, 'error');
      }
    }
  };

  const handleViewStoreDetails = (storeId: string) => {
    // Navigate to store details page
    window.location.href = `/stores/${storeId}`;
  };

  const getClusterName = (clusterId: string | undefined) => {
    if (!clusterId) return 'Unknown';
    const cluster = clusters.find(c => c.id === clusterId);
    return cluster ? cluster.name : 'Unknown';
  };

  const getManagerName = (store: Store) => {
    // First try to use the manager object if it's already included in the store
    if (store.manager) {
      return `${store.manager.first_name} ${store.manager.last_name}`;
    }
    // Fallback to looking up by manager_id
    if (!store.manager_id) return 'N/A';
    const manager = storeManagers.find(m => m.id === store.manager_id);
    return manager ? `${manager.first_name} ${manager.last_name}` : 'N/A';
  };

  if (loading) {
    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ color: '#081122', fontWeight: 600, mb: 1 }}>
            Store Management
          </Typography>
          <Typography variant="body1" sx={{ color: '#666666' }}>
            Manage stores, locations, and assignments
          </Typography>
        </Box>
        <TableShimmer rows={6} columns={8} />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ color: '#081122', fontWeight: 600, mb: 1 }}>
            Stores Management
          </Typography>
          <Typography variant="body1" sx={{ color: '#666666' }}>
            Manage your store locations and details
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenDialog(true)}
          sx={{
            background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
            },
            boxShadow: '0 4px 12px rgba(8, 17, 34, 0.3)',
            px: 3,
            py: 1.5,
          }}
        >
          Add Store
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search stores by name or code..."
              value={filters.search}
              onChange={(e) => {
                setFilters({ ...filters, search: e.target.value, page: 1 });
              }}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                endAdornment: filters.search && (
                  <IconButton
                    size="small"
                    onClick={() => setFilters({ ...filters, search: '', page: 1 })}
                  >
                    <Clear />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Cluster</InputLabel>
              <Select
                value={filters.cluster_id}
                onChange={(e) => {
                  setFilters({ ...filters, cluster_id: e.target.value, page: 1 });
                }}
                label="Filter by Cluster"
              >
                <MenuItem value="">All Clusters</MenuItem>
                {clusters.map((cluster) => (
                  <MenuItem key={cluster.id} value={cluster.id}>
                    {cluster.name} ({cluster.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Items per page</InputLabel>
              <Select
                value={filters.limit}
                onChange={(e) => {
                  setFilters({ ...filters, limit: Number(e.target.value), page: 1 });
                }}
                label="Items per page"
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Cluster</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Manager</TableCell>
              <TableCell>Total Users</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>View Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stores.map((store) => (
              <TableRow key={store.id}>
                <TableCell>
                  <Chip
                    label={store.code}
                    color="secondary"
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{store.name}</TableCell>
                <TableCell>
                  <Chip
                    label={getClusterName(store.cluster_id)}
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{store.address || 'N/A'}</TableCell>
                <TableCell>{getManagerName(store)}</TableCell>
                <TableCell>
                  <Chip
                    label={store.userCount || 0}
                    color="info"
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{new Date(store.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleViewStoreDetails(store.id)}
                    title="View Store Details"
                    color="primary"
                  >
                    <Visibility />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {pagination.total > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} stores
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              disabled={pagination.page === 1}
              onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
            >
              Previous
            </Button>
            <Typography variant="body2" sx={{ px: 2 }}>
              Page {pagination.page} of {pagination.pages}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              disabled={pagination.page >= pagination.pages}
              onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}

      {/* Create Store Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: '#4caf50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white'
            }}>
              <StoreIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
                Create New Store
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666' }}>
                Add a new store location with detailed information
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        <form onSubmit={handleSubmit(handleCreateStore)}>
          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('name')}
                  label="Store Name"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  placeholder="e.g., Central Mall Store"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('code')}
                  label="Store Code"
                  fullWidth
                  error={!!errors.code}
                  helperText={errors.code?.message}
                  placeholder="e.g., CMS001"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.cluster_id}>
                  <InputLabel>Cluster</InputLabel>
                  <Select
                    {...register('cluster_id')}
                    label="Cluster"
                    sx={{
                      borderRadius: 2,
                    }}
                  >
                    {clusters.map((cluster) => (
                      <MenuItem key={cluster.id} value={cluster.id}>
                        {cluster.name} ({cluster.code})
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.cluster_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {errors.cluster_id.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('city')}
                  label="City"
                  fullWidth
                  error={!!errors.city}
                  helperText={errors.city?.message}
                  placeholder="e.g., Delhi"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('state')}
                  label="State"
                  fullWidth
                  error={!!errors.state}
                  helperText={errors.state?.message}
                  placeholder="e.g., Delhi"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  {...register('description')}
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  placeholder="Optional description about the store..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.manager_id}>
                  <InputLabel>Store Manager (Optional)</InputLabel>
                  <Select
                    {...register('manager_id')}
                    label="Store Manager (Optional)"
                    sx={{
                      borderRadius: 2,
                    }}
                  >
                    <MenuItem value="">
                      <em>None - Assign later</em>
                    </MenuItem>
                    {storeManagers.map((manager) => (
                      <MenuItem key={manager.id} value={manager.id}>
                        {manager.first_name} {manager.last_name} ({manager.employee_id})
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.manager_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {errors.manager_id.message}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ mt: 0.5, ml: 1.75, color: 'text.secondary' }}>
                    Store Manager can be assigned later. If not provided, Super Admin will be assigned temporarily.
                  </Typography>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <LocationInput
                  selectedLocation={selectedLocation}
                  onLocationSelect={setSelectedLocation}
                />
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            pt: 2, 
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#f8f9fa',
            gap: 2
          }}>
            <Button 
              onClick={() => {
                setOpenDialog(false);
                setSelectedLocation(null);
                reset();
              }}
              variant="outlined"
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1,
                borderColor: '#d0d0d0',
                color: '#666666',
                '&:hover': {
                  borderColor: '#999999',
                  backgroundColor: '#f5f5f5',
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              size="large"
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
                },
                boxShadow: '0 4px 12px rgba(8, 17, 34, 0.3)',
              }}
            >
              Create Store
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StoresPage;
